import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { outsourcingApi } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { Hotel, Plus, Loader2, BedDouble, DollarSign, Users } from 'lucide-react';

const ETB = (n) => `ETB ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const STATUS = { PENDING: 'bg-amber-100 text-amber-700', CONFIRMED: 'bg-blue-100 text-blue-700', COMPLETED: 'bg-emerald-100 text-emerald-700', CANCELLED: 'bg-red-100 text-red-600' };

export default function OutsourcingPage() {
  const { currentProperty } = useAuth();
  const pid = currentProperty?.id;
  const [tab, setTab] = useState('records');
  const [dash, setDash] = useState(null);
  const [records, setRecords] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recDialog, setRecDialog] = useState(false);
  const [partDialog, setPartDialog] = useState(false);

  const load = useCallback(async () => {
    if (!pid) return;
    try {
      const [d, r, p] = await Promise.all([outsourcingApi.dashboard(pid), outsourcingApi.listRecords(pid), outsourcingApi.listPartners(pid)]);
      setDash(d.data); setRecords(r.data || []); setPartners(p.data || []);
    } catch (e) { toast.error('Failed to load outsourcing data'); }
    finally { setLoading(false); }
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => { try { await outsourcingApi.updateRecord(id, { status }); toast.success('Updated'); load(); } catch (e) { toast.error('Failed'); } };

  if (!currentProperty) return <div className="p-6"><Card><CardContent className="p-8 text-center text-slate-500">Select a property.</CardContent></Card></div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-indigo-600 text-white flex items-center justify-center"><Hotel className="h-6 w-6" /></div>
          <div><h1 className="text-2xl font-bold text-slate-900">Outsourcing / Overflow</h1><p className="text-sm text-slate-500">When overbooked, send guests to partner hotels &amp; track it</p></div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4 flex items-center gap-3"><BedDouble className="h-5 w-5 text-slate-500" /><div><p className="text-xs text-slate-500">Rooms Outsourced</p><p className="text-xl font-semibold">{dash?.rooms_outsourced ?? 0}</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><Users className="h-5 w-5 text-blue-500" /><div><p className="text-xs text-slate-500">Guests</p><p className="text-xl font-semibold">{dash?.guests_outsourced ?? 0}</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><DollarSign className="h-5 w-5 text-emerald-500" /><div><p className="text-xs text-slate-500">Total Cost</p><p className="text-xl font-semibold">{ETB(dash?.total_cost)}</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><Hotel className="h-5 w-5 text-indigo-500" /><div><p className="text-xs text-slate-500">Partners</p><p className="text-xl font-semibold">{partners.length}</p></div></CardContent></Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList><TabsTrigger value="records">Overflow Records</TabsTrigger><TabsTrigger value="partners">Partner Hotels</TabsTrigger></TabsList>

        <TabsContent value="records" className="space-y-3">
          <div className="flex justify-end"><Button onClick={() => setRecDialog(true)} disabled={partners.length === 0}><Plus className="h-4 w-4 mr-2" /> New Overflow</Button></div>
          {partners.length === 0 && <p className="text-xs text-slate-500 text-right">Add a partner hotel first.</p>}
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Partner</TableHead><TableHead>Dates</TableHead><TableHead className="text-right">Rooms</TableHead><TableHead className="text-right">Guests</TableHead><TableHead className="text-right">Cost</TableHead><TableHead>Reason</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {loading ? <TableRow><TableCell colSpan={7} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></TableCell></TableRow>
                  : records.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">No overflow records.</TableCell></TableRow>
                  : records.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-medium">{r.partner_hotels?.name || '—'}</TableCell>
                      <TableCell className="text-sm">{r.check_in} → {r.check_out}</TableCell>
                      <TableCell className="text-right">{r.rooms_count}</TableCell>
                      <TableCell className="text-right">{r.guests_count}</TableCell>
                      <TableCell className="text-right">{ETB(r.total_cost)}</TableCell>
                      <TableCell className="max-w-[160px] truncate text-sm text-slate-500">{r.reason}</TableCell>
                      <TableCell>
                        <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
                          <SelectTrigger className="h-7 w-[130px]"><Badge className={STATUS[r.status]}>{r.status}</Badge></SelectTrigger>
                          <SelectContent>{Object.keys(STATUS).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="partners" className="space-y-3">
          <div className="flex justify-end"><Button variant="outline" onClick={() => setPartDialog(true)}><Plus className="h-4 w-4 mr-2" /> Add Partner</Button></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {partners.map((p) => (
              <Card key={p.id}><CardContent className="p-4">
                <p className="font-semibold">{p.name}</p>
                <p className="text-xs text-slate-500">{p.location}</p>
                <p className="text-xs text-slate-500 mt-1">{p.contact_name} {p.contact_phone}</p>
              </CardContent></Card>
            ))}
            {partners.length === 0 && <p className="text-sm text-slate-500">No partner hotels yet.</p>}
          </div>
        </TabsContent>
      </Tabs>

      <RecordDialog open={recDialog} onOpenChange={setRecDialog} pid={pid} partners={partners} onSaved={() => { setRecDialog(false); load(); }} />
      <PartnerDialog open={partDialog} onOpenChange={setPartDialog} pid={pid} onSaved={() => { setPartDialog(false); load(); }} />
    </div>
  );
}

function RecordDialog({ open, onOpenChange, pid, partners, onSaved }) {
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF({ partner_hotel_id: '', rooms_count: '', guests_count: '', check_in: '', check_out: '', rate_per_night: '', reason: '' }); }, [open]);
  const save = async () => {
    if (!f.partner_hotel_id) { toast.error('Select a partner hotel'); return; }
    try {
      setSaving(true);
      await outsourcingApi.createRecord(pid, { partner_hotel_id: f.partner_hotel_id, rooms_count: parseInt(f.rooms_count) || 0, guests_count: parseInt(f.guests_count) || 0, check_in: f.check_in || null, check_out: f.check_out || null, rate_per_night: parseFloat(f.rate_per_night) || 0, reason: f.reason || null });
      toast.success('Overflow recorded'); onSaved();
    } catch (e) { toast.error('Failed'); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="max-w-md">
      <DialogHeader><DialogTitle>New Overflow Record</DialogTitle><DialogDescription>Cost = rate × rooms × nights (auto)</DialogDescription></DialogHeader>
      <div className="space-y-3">
        <div className="space-y-1.5"><Label>Partner Hotel</Label>
          <Select value={f.partner_hotel_id} onValueChange={(v) => setF({ ...f, partner_hotel_id: v })}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Rooms</Label><Input type="number" value={f.rooms_count || ''} onChange={(e) => setF({ ...f, rooms_count: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Guests</Label><Input type="number" value={f.guests_count || ''} onChange={(e) => setF({ ...f, guests_count: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Check-in</Label><Input type="date" value={f.check_in || ''} onChange={(e) => setF({ ...f, check_in: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Check-out</Label><Input type="date" value={f.check_out || ''} onChange={(e) => setF({ ...f, check_out: e.target.value })} /></div>
          <div className="space-y-1.5 col-span-2"><Label>Rate / night</Label><Input type="number" value={f.rate_per_night || ''} onChange={(e) => setF({ ...f, rate_per_night: e.target.value })} /></div>
        </div>
        <div className="space-y-1.5"><Label>Reason</Label><Input value={f.reason || ''} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="Overbooked" /></div>
      </div>
      <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button><Button onClick={save} disabled={saving}>Save</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}

function PartnerDialog({ open, onOpenChange, pid, onSaved }) {
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF({ name: '', location: '', contact_name: '', contact_phone: '' }); }, [open]);
  const save = async () => {
    if (!f.name?.trim()) { toast.error('Enter a name'); return; }
    try { setSaving(true); await outsourcingApi.createPartner(pid, f); toast.success('Partner added'); onSaved(); }
    catch (e) { toast.error('Failed'); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="max-w-md">
      <DialogHeader><DialogTitle>Add Partner Hotel</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div className="space-y-1.5"><Label>Name</Label><Input value={f.name || ''} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
        <div className="space-y-1.5"><Label>Location</Label><Input value={f.location || ''} onChange={(e) => setF({ ...f, location: e.target.value })} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Contact</Label><Input value={f.contact_name || ''} onChange={(e) => setF({ ...f, contact_name: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Phone</Label><Input value={f.contact_phone || ''} onChange={(e) => setF({ ...f, contact_phone: e.target.value })} /></div>
        </div>
      </div>
      <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button><Button onClick={save} disabled={saving}>Save</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}
